import React, { FunctionComponent, useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./UserVideoLessonDetail.scss";
import { LessonInfoTab } from "./Tabs/LessonTab";
import { NotesTab } from "./Tabs/NotesTab";
import { AskElliotTab } from "./Tabs/AskElliotTab";
import { PracticeTab } from "./Tabs/PracticeTab";
import { LessonsSidebar } from "./LessonsSidebar";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { listVideoLessonsLists } from "../../Redux/Slices/UserVideoLessons/ListVideoLessonsList";
import { ListGetVideoLesson } from "../../Redux/Slices/UserVideoLessons/ListGetVideoLessons";
import useVdoCipher from "../../Hooks/useVdoChipper";
import DiscussionTab from "./Tabs/Discussion/Discussion";
import CloseIcon from "@mui/icons-material/Close";
import {
  clearProgressUpdateVideoLessons,
  ProgressUpdateVideoLesson,
} from "../../Redux/Slices/UserVideoLessons/ProgressUpdateVideoLessons";
import {
  Box,
  Button,
  Paper,
  Typography,
  Stack,
  Avatar,
  useMediaQuery,
  useTheme,
  IconButton,
  Grid,
  Card,
  CardContent,
  SvgIcon,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AvatarImage from "../../Assets/Images/Chatbot_Avatar.png";
// import { ReactComponent as AverageScore } from "../../Assets/Images/Average_Score.svg";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import ChatbotPracticeEvaluation from "../../Components/ChatbotPracticeEvaluation/ChatpracticeEvaluation";
import { clearChatpracticeEvaluationStatus } from "../../Redux/Slices/ChatbotpracticeEvaluation/ChatpracticeEvaluationSlicer";
import AnalyticsTab from "./Tabs/Analytics/Analytics";
import {
  clearVideoLessonAnalytics,
  VideoLessonAnalytics,
} from "../../Redux/Slices/UserVideoLessons/VideoLessonAnalytics";
import ChatbotPublic from "../../Components/ChatbotPublic/ChatbotPublic";
import { clearAskAIVideoLessonsResponse } from "../../Redux/Slices/UserVideoLessons/AskAIVideoLessons";
import { ReactComponent as AverageScore } from "../../Assets/Images/AverageScore.svg";
import { ReactComponent as studyBreakColor } from "../../Assets/Images/studyBreakColor.svg";

const IconClock = () => <span className="icon-placeholder">🕒</span>;
const IconChat = () => <span className="icon-placeholder">💬</span>;

declare global {
  interface Window {
    VdoPlayer: any;
    player: any;
  }
}

interface Lesson {
  id: number;
  title: string;
  description: string;
  completed?: boolean;
  duration: string;
  lession_length?: number;
  complete_lession_length?: number;
  video_count?: number;
  video_play_time?: number;
  videoUrl: string;
  progress: string;
  watchTime: string;
  averageScore: string;
  studyStreak: string;
  transcription: string;
  is_favorite?: boolean;
  is_watch_later?: boolean;
  last_position?: number;
}

const UserVideoLessonDetail: FunctionComponent = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { subjectId, topicId, topicName } =
    (location && (location.state as any)) || {};

  const dispatch = useAppDispatch();
  const { data: topicFromAPI } = useAppSelector(
    (state) => (state as any).ListVideoLessonsListSlices
  );
  const { data: videoFromAPI } = useAppSelector(
    (state) => (state as any).ListGetVideoLessonsSliceReducer
  );
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [autoplay, setAutoplay] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState("lesson");
  const [isTopBannerVisible, setTopBannerVisible] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlayerInitialized, setIsPlayerInitialized] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(true);

  const [durationTime, setDurationTime] = useState<number>(0);

  const tabContentRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<any>(null);
  const [currentTopicId, setCurrentTopicId] = useState<string | null>(null);

  const { loadVideo, isAPIReady } = useVdoCipher();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [chatbotOpen, setChatbotOpen] = React.useState<boolean>(false);

  const [initialMessage, setInitialMessage] = useState<string | undefined>(undefined);
  const autoplayRef = useRef<boolean>(autoplay);

  const name =
    topicName?.toLowerCase().includes("writing") ? "Writing" :
      topicName?.toLowerCase().includes("reading") ? "Reading" :
        topicName?.toLowerCase().includes("abstract") ? "Abstract" :
          topicName?.toLowerCase().includes("quantitative") ? "Quantitative" :
            topicName?.toLowerCase().startsWith("ar") ? "Arithmetic" :
              topicName || "Lesson";

  // Handle Ask Elliot button click
  const handleAskElliotClick = (msg?: string) => {
    setInitialMessage(msg);
    setChatbotOpen(true);
  };

  function formatTime(totalSeconds: number | string) {
    if (totalSeconds == null) return "00h 00m 00s";

    let seconds = 0;

    if (typeof totalSeconds === "string" && totalSeconds.includes(":")) {
      // case: input is "hh:mm:ss"
      const parts = totalSeconds.split(":").map((v) => parseInt(v, 10));
      if (parts.length === 3) {
        seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
      } else if (parts.length === 2) {
        seconds = parts[0] * 60 + parts[1];
      }
    } else {
      seconds = Number(totalSeconds);
    }

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    return `${String(h).padStart(2, "0")}h ${String(m).padStart(
      2,
      "0"
    )}m ${String(s).padStart(2, "0")}s`;
  }

  // Fetch lessons list
  useEffect(() => {
    if (topicId) {
      dispatch(listVideoLessonsLists({ topic_id: topicId }));
    }
  }, [dispatch, topicId]);

  const fetchLessons = (topicFromAPIData: any) => {
    if (topicFromAPIData?.data?.videos) {
      console.log(topicFromAPIData, "video.last_position");
      var videoCount = 1;
      const fetchedLessons = topicFromAPIData.data.videos.map((video: any) => ({
        id: video.id,
        video_count: videoCount++,
        title: video.title,
        description: video.description,
        duration: video.duration?.toString(),
        videoUrl: "",
        lession_length: topicFromAPIData?.data?.videos?.length,
        complete_lession_length: topicFromAPIData?.data?.videos
          ?.filter((val: any) => val.completed === true)
          .map((val: any) => ({ ...val })).length,
        completed: video.completed,
        progress: topicFromAPIData?.data?.progress?.toString() ?? "0%",
        watchTime:
          topicFromAPIData?.data?.total_duration?.toString() ?? "0 min",
        averageScore: topicFromAPIData?.data?.average_score,
        studyStreak: topicFromAPIData?.data?.streak?.toString() ?? "0",
        transcription: video.transcription ?? "",
        is_favorite: video.is_favorite ?? false,
        is_watch_later: video.is_watch_later ?? false,
        last_position: video.last_position ?? 0,
      }));
      console.log("fetchedLessons", fetchedLessons);

      setLessons(fetchedLessons);
    }
  };
  useEffect(() => {
    if (topicFromAPI) {
      fetchLessons(topicFromAPI);
    }
  }, [topicFromAPI]);

  useEffect(() => {
    if (lessons.length > 0) {
      const lessonFromUrl = lessons.find((l) => l.id.toString() === lessonId);
      if (!currentLesson) {
        setCurrentLesson(lessonFromUrl || lessons[0]);
      }
    }
  }, [lessons, lessonId]);

  // Dispatch video fetch when current lesson changes
  useEffect(() => {
    if (currentLesson?.id) {
      setShouldLoadVideo(true);
      setIsPlayerInitialized(false);
      dispatch(
        ListGetVideoLesson({
          id: currentLesson.id,
          topic_id: topicId,
        })
      );
    }
  }, [currentLesson?.id, dispatch, topicId]);

  const [loadedLessonId, setLoadedLessonId] = useState<number | null>(null);

  const getNextLesson = (lesson: Lesson | null) => {
    if (!lesson) return null;
    const idx = lessons.findIndex((l) => l.id === lesson.id);
    return lessons[idx + 1] || null;
  };

  useEffect(() => {
    autoplayRef.current = autoplay;
  }, [autoplay]);

  useEffect(() => {
    if (
      !videoFromAPI?.data?.otp ||
      !videoFromAPI?.data?.playbackInfo ||
      !isAPIReady ||
      !currentLesson?.id
    ) {
      return;
    }

    // A new video is selected or the first video is being loaded
    if (videoFromAPI.data.video_id !== loadedLessonId) {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.warn("Error cleaning up old player:", e);
        }
        playerRef.current = null;
      }

      if (videoContainerRef.current) {
        videoContainerRef.current.innerHTML = "";
      }

      const initializePlayer = async () => {
        try {
          await loadVideo({
            otp: videoFromAPI!.data!.otp,
            playbackInfo: videoFromAPI!.data!.playbackInfo,
            container: videoContainerRef.current!,
          });

          const iframe = videoContainerRef.current?.querySelector("iframe");
          if (iframe) {
            playerRef.current = new window.VdoPlayer(iframe);
            playerRef.current.otp = videoFromAPI?.data?.otp;

            window.player = playerRef.current;

            // Resume from last position when video metadata is ready
            playerRef.current.video.addEventListener("loadedmetadata", () => {
              if (videoFromAPI?.data?.last_position) {
                playerRef.current.video.currentTime = videoFromAPI?.data?.last_position;
              }
            });

            // Track current playback time
            playerRef.current.video.addEventListener("timeupdate", () => {
              const time = playerRef.current.video.currentTime;
              const duration = playerRef?.current?.video?.duration;
              setCurrentTime(time);
              setDurationTime(duration);

              if (currentLesson?.id) {
                setLessons((prevLessons) =>
                  prevLessons.map((lessons) =>
                    lessons.id === currentLesson?.id
                      ? {
                        ...lessons,
                        video_play_time: duration
                          ? (time / duration) * 100
                          : 0,
                      }
                      : lessons
                  )
                );
              }
            });
            // playerRef?.current?.video?.addEventListener(
            //   "loadedmetadata",
            //   () => {
            //     setDurationTime(playerRef?.current?.video?.duration);
            //   }
            // );

            // Save progress when paused
            playerRef.current.video.addEventListener("pause", async () => {
              if (currentLesson?.id) {
                try {
                  const time = playerRef.current.video.currentTime;
                  const duration = playerRef.current.video.duration || 0;

                  if (Math.floor(time) != 0) {
                    dispatch(
                      ProgressUpdateVideoLesson({
                        video_id: videoFromAPI?.data?.video_id ?? 0,
                        last_position: Math.floor(time),
                        completed: duration > 0 && time >= duration - 5,
                      })
                    );
                  }
                } catch (err) {
                  console.error("Error saving progress on pause", err);
                }
              }
            });

            playerRef.current.video.addEventListener("ended", () => {
              if (autoplayRef.current) {
                const next = getNextLesson(currentLesson);
                if (next) {
                  setCurrentLesson(next);
                } else {
                  console.log("lesson is not exit");
                }
              } else {
                console.log("the auto play off");
              }
            });

            setLoadedLessonId(videoFromAPI.data.video_id);
          }
        } catch (err) {
          console.error("Player initialization error:", err);
        }
      };

      initializePlayer();

    }
  }, [
    videoFromAPI?.data?.otp,
    videoFromAPI?.data?.playbackInfo,
    isAPIReady,
    loadVideo,
    currentLesson?.id,
    videoFromAPI?.data?.video_id,
    autoplay,
  ]);



  useEffect(() => {
    const saveProgress = () => {
      if (!playerRef.current || !currentLesson?.id) return;

      const time = playerRef.current.video.currentTime;
      const duration = playerRef.current.video.duration || 0;
      if (Math.floor(time) != 0) {
        dispatch(
          ProgressUpdateVideoLesson({
            video_id: videoFromAPI?.data?.video_id ?? 0,
            last_position: Math.floor(time),
            completed: duration > 0 && time >= duration - 5,
          })
        );
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        saveProgress();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      saveProgress();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [currentLesson?.id, dispatch]);

  const handleLessonClick = (lesson: Lesson) => {
    if (currentLesson?.id !== lesson.id) {
      setCurrentLesson(lesson);
      setActiveTab("lesson");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (lessons.length > 0) {
      const lessonFromUrl = lessons.find((l) => l.id.toString() === lessonId);

      // Check if we switched to a different topic
      const hasTopicChanged = currentTopicId !== topicId;

      if (hasTopicChanged) {
        // Topic changed - reset to first lesson or URL lesson
        setCurrentLesson(lessonFromUrl || lessons[0]);
        setCurrentTopicId(topicId);
      } else if (!currentLesson) {
        // Same topic but no current lesson - set initial lesson
        setCurrentLesson(lessonFromUrl || lessons[0]);
      } else if (lessonFromUrl && currentLesson.id !== lessonFromUrl.id) {
        // Same topic but URL changed - update to URL lesson
        setCurrentLesson(lessonFromUrl);
      } else {
        // Check if current lesson exists in new lessons array
        const currentLessonExists = lessons.some(l => l.id === currentLesson.id);
        if (!currentLessonExists) {
          // Current lesson doesn't exist in new lessons - reset to first
          setCurrentLesson(lessons[0]);
        }
      }
    }
  }, [lessons, lessonId, topicId, currentLesson, currentTopicId]);

  useEffect(() => {
    if (videoContainerRef.current) {
      videoContainerRef.current.innerHTML = "";
    }

    if (playerRef.current) {
      try {
        playerRef.current.destroy?.();
      } catch (err) {
        console.warn("Error destroying player on topic change:", err);
      }
      playerRef.current = null;
    }

    setLoadedLessonId(null);
    setIsPlayerInitialized(false);
  }, [topicId]);


  useEffect(() => {
    setLessons([]);
    setCurrentLesson(null);
  }, [topicId]);




  useEffect(() => {
    dispatch(VideoLessonAnalytics({ id: topicId }));
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearVideoLessonAnalytics());
      dispatch(clearProgressUpdateVideoLessons());
    };
  }, []);

  const renderTabContent = () => {
    if (!currentLesson) return null;
    switch (activeTab) {
      case "lesson":
        return (
          <LessonInfoTab
            topicId={topicId}
            currentLesson={currentLesson}
            switchToAskElliotTab={handleAskElliotClick}
          />
        );
      case "notes":
        return (
          <NotesTab currentVideoTime={currentTime} videoid={currentLesson.id} />
        );
      // case "analytics":
      //   return <AnalyticsTab topicId={topicId} />;
      case "practice":
        return <PracticeTab
          name={name} />;
      case "discussion":
        return <DiscussionTab videoId={videoFromAPI?.data?.video_id ?? 0} />;
      // case "achievements":
      //   return <div>Content for achievements tab.</div>;
      default:
        return <div>Content for {activeTab} tab.</div>;
    }
  };

  if (!currentLesson) return <div>Loading lesson details...</div>;

  const checkCompletedLession = lessons
    ?.filter((val) => val.completed === true)
    .map((val) => ({ ...val }));

  return (
    <Box className="reading-comprehension-container">
      <div className="header-bar">
        <button
          className="back-button"
          onClick={() =>
            // navigate("/videolessonstopic", { state: { subjectId } })
            navigate(-1)
          }
        >
          ←
        </button>
        <div className="header-title">
          <h2>{topicName}</h2>
          <p>GATE Exam Preparation</p>
        </div>
      </div>

      {isTopBannerVisible && (
        <>
          <Paper className="help-banner" elevation={0}>
            <Box className="banner-content">
              {/* Left Section */}
              <Box className="banner-left">
                <div className="blinking-avatar-container3">
                  <Avatar
                    src={AvatarImage}
                    alt="Profile"
                    className="blinking-avatar3"
                  />
                </div>
                <Box className="banner-text">
                  <Typography variant="subtitle2" className="banner-title">
                    Need help with this lesson?
                  </Typography>
                  <Typography variant="body2" className="banner-subtitle">
                    I'm here to help you understand {name} concepts better!
                  </Typography>
                </Box>
              </Box>

              {/* Right Section */}
              <Box className="banner-actions" display={"flex"}>
                <Button
                  variant="contained"
                  className="ask-elliot-button"
                  onClick={() => handleAskElliotClick()}
                  sx={{
                    boxShadow: "none",
                    height: "36px",
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "20px",
                  }}
                >
                  Ask Elliot
                </Button>
                <IconButton className="close-banner-button">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </>
      )}

      {/* ask Elliot flot button start */}

      {chatbotOpen && (
        <ChatbotPublic
          open={chatbotOpen}
          setOpen={setChatbotOpen}
          video_id={currentLesson?.id}
          initialMessage={initialMessage}
        />
      )}

      <button
        className="floating-chatbot-button "
        onClick={() => {
          handleAskElliotClick();
          dispatch(clearAskAIVideoLessonsResponse());
        }}
      >
        <img
          src={AvatarImage}
          alt="Chat with Elliot"
          className="chatbot-avatar"
        />
      </button>
      {/* end */}

      <Grid item className="stats-cards">
        <Card className="stat-card">
          <CardContent className="card_content" sx={{ padding: "24px 0px" }}>
            <Typography
              fontSize={"14px"}
              fontWeight={"500"}
              lineHeight={"20px"}
              color={"#4b5563"}
            >
              Total Watch Time
            </Typography>
            <Typography
              fontWeight={700}
              fontSize={"24px"}
              display={"flex"}
              alignItems={"center"}
              gap={"5px"}
              lineHeight={"32px"}
              color={"#020817"}
            >
              <AccessTimeIcon sx={{ color: "#3b82f6" }} />
              {formatTime(currentLesson.watchTime)}
            </Typography>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="card_content" sx={{ padding: "24px 0px" }}>
            <Typography
              fontSize={"14px"}
              fontWeight={"500"}
              lineHeight={"20px"}
              color={"#4b5563"}
            >
              Progress
            </Typography>
            <Typography width={"100%"}>
              <Typography
                fontWeight={400}
                fontSize={20}
                display={"flex"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {checkCompletedLession?.length}/{lessons.length} lessons
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {currentLesson.progress}%
                </span>
              </Typography>
              <div
                style={{
                  width: "100%",
                  position: "relative",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "8px",
                  overflow: "hidden",
                  height: "8px",
                  marginTop: "4px",
                }}
              >
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${parseInt(currentLesson.progress)}%`,
                    backgroundColor: "#3b82f6",
                    height: "100%",
                    borderRadius: "8px",
                    transition: "width 0.3s ease-in-out",
                  }}
                ></div>
              </div>
            </Typography>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="card_content" sx={{ padding: "24px 0px" }}>
            <Typography
              fontSize={"14px"}
              fontWeight={"500"}
              lineHeight={"20px"}
              color={"#4b5563"}
            >
              Average Score
            </Typography>
            <Typography
              fontWeight={700}
              fontSize={"24px"}
              lineHeight={"32px"}
              display={"flex"}
              alignItems={"center"}
              gap={"10px"}
            >
              <AverageScore />
              {currentLesson?.averageScore ? `${currentLesson.averageScore}%` : "0%"}
            </Typography>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="card_content" sx={{ padding: "24px 0px" }}>
            <Typography
              fontSize={"14px"}
              fontWeight={"500"}
              lineHeight={"20px"}
              color={"#4b5563"}
            >
              Study Streak
            </Typography>
            <Typography
              fontWeight={700}
              fontSize={"24px"}
              lineHeight={"32px"}
              display={"flex"}
              gap={"5px"}
              alignItems={"center"}
            >
              <SvgIcon
                component={studyBreakColor}
                inheritViewBox
                sx={{ fill: "none" }}
              />
              {currentLesson.studyStreak} days
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Box className="lesson-content-layout">
        <Box className="main-content-area">
          <Box className="video-player-container" sx={{ height: "100%" }}>
            <div
              ref={videoContainerRef}
              style={{ width: "100%", height: "100%" }}
            ></div>
          </Box>
        </Box>
        <LessonsSidebar
          lessons={lessons}
          currentLesson={currentLesson}
          onLessonClick={handleLessonClick}
          autoplay={autoplay}
          setAutoplay={setAutoplay}
        />
        <div className="lesson_main_block">
          <Box className="lesson-tabs-wrapper">
            <Box className="lesson-tabs">
              {[
                "lesson",
                "notes",
                "practice",
                "discussion",
                // "analytics",
                // "achievements",
              ].map((tab) => (
                <button
                  key={tab}
                  className={activeTab === tab ? "active" : ""}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === "askElliot" ? <IconChat /> : null}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </Box>
          </Box>
          <div>
            <Box className="lesson-info-section" ref={tabContentRef}>
              {renderTabContent()}
            </Box>
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default UserVideoLessonDetail;
